import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService, getToken, isAuthenticated } from '../../../services';
import { utils } from '../../../utils';
import FormLayout from '../../../components/Common/FormLayout';

const ChecklistGroupCreate: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    tipo: 'Veículo',
    categoria: '',
    ativo: true,
    ordem: 0
  });

  const [errors, setErrors] = useState<Record<string, string | undefined>>({});

  // Verificar autenticação ao montar o componente
  useEffect(() => {
    const token = getToken();
    const authenticated = isAuthenticated();

    if (!token || !authenticated) {
      utils.showNotification('Você precisa estar autenticado para acessar esta página.', { type: 'warning' });
      navigate('/login');
    }
  }, [navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : (type === 'number' ? parseInt(value) || 0 : value)
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    }
    if (!formData.tipo.trim()) {
      newErrors.tipo = 'Tipo é obrigatório';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const token = localStorage.getItem('confereai_token');
    if (!token) {
      utils.showNotification('Sessão expirada. Por favor, faça login novamente.', { type: 'error' });
      navigate('/login');
      return;
    }

    if (!validateForm()) {
      utils.showNotification('Por favor, corrija os erros no formulário', { type: 'error' });
      return;
    }

    setIsLoading(true);

    const payload = {
      nome: formData.nome,
      descricao: formData.descricao || undefined,
      tipo: formData.tipo,
      categoria: formData.categoria || undefined,
      ordem: formData.ordem,
      status: formData.ativo ? 'Ativo' : 'Inativo'
    };

    try {
      const response = await apiService.createChecklistGroup(payload);

      if (response.success) {
        utils.showNotification('Grupo criado! Agora vincule os itens de checklist ao grupo.', { type: 'success' });
        const newGroupId = response.data?.id;
        if (newGroupId) {
          navigate(`/grupos-checklist/editar/${newGroupId}`);
        } else {
          navigate('/grupos-checklist');
        }
      } else {
        throw new Error(response.message || 'Erro ao criar grupo');
      }
    } catch (error: any) {
      utils.showNotification(error.message || 'Erro ao criar grupo', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = () => {
    const fakeEvent = {
      preventDefault: () => {}
    } as React.FormEvent;
    handleSubmit(fakeEvent);
  };

  const handleCancel = () => {
    navigate('/grupos-checklist');
  };

  return (
    <FormLayout
      title="Novo Grupo de Checklist"
      subtitle="Cadastro de Grupo de Checklist"
      onCancel={handleCancel}
      onSave={handleSave}
      saveButtonText="Cadastrar Grupo"
      isLoading={isLoading}
      showActions={false}
    >
      <form onSubmit={handleSubmit}>
        {/* Informações Básicas */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Informações do Grupo
          </h3>

          <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <div className="form-group">
              <label className="form-label">Nome *</label>
              <input
                type="text"
                name="nome"
                value={formData.nome}
                onChange={handleInputChange}
                className={`form-control ${errors.nome ? 'error' : ''}`}
                placeholder="Ex: Inspeção Visual Externa"
              />
              {errors.nome && <span className="error-message">{errors.nome}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Tipo *</label>
              <select
                name="tipo"
                value={formData.tipo}
                onChange={handleInputChange}
                className={`form-control ${errors.tipo ? 'error' : ''}`}
              >
                <option value="Veículo">Veículo</option>
                <option value="Documental">Documental</option>
                <option value="Equipamento">Equipamento</option>
                <option value="Operacional">Operacional</option>
                <option value="Manutenção">Manutenção</option>
              </select>
              {errors.tipo && <span className="error-message">{errors.tipo}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Categoria</label>
              <select
                name="categoria"
                value={formData.categoria}
                onChange={handleInputChange}
                className="form-control"
              >
                <option value="">Selecione uma categoria</option>
                <option value="Segurança">Segurança</option>
                <option value="Mecânica">Mecânica</option>
                <option value="Elétrica">Elétrica</option>
                <option value="Hidráulica">Hidráulica</option>
                <option value="Estrutural">Estrutural</option>
                <option value="Legal">Legal</option>
                <option value="Operacional">Operacional</option>
                <option value="Manutenção">Manutenção</option>
                <option value="Preventiva">Preventiva</option>
                <option value="Corretiva">Corretiva</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Ordem</label>
              <input
                type="number"
                name="ordem"
                value={formData.ordem}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Ex: 1"
                min="0"
              />
            </div>
          </div>

          <div className="form-group" style={{ marginTop: '20px' }}>
            <label className="form-label">Descrição</label>
            <textarea
              name="descricao"
              value={formData.descricao}
              onChange={handleInputChange}
              className="form-control"
              placeholder="Descrição do grupo de checklist..."
              rows={4}
            />
          </div>

          <div className="form-group" style={{ marginTop: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input
              type="checkbox"
              name="ativo"
              id="ativo"
              checked={formData.ativo}
              onChange={handleInputChange}
              style={{ width: 'auto', margin: 0 }}
            />
            <label htmlFor="ativo" className="form-label" style={{ margin: 0 }}>
              Grupo Ativo
            </label>
          </div>
        </div>

        {/* Ações do Formulário */}
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="loading"></span>
                Cadastrando...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                Cadastrar Grupo
              </>
            )}
          </button>
        </div>
      </form>
    </FormLayout>
  );
};

export default ChecklistGroupCreate;
